import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import {
  Users, Plus, Search, Upload, Download, Mail, Phone, MapPin,
  Building2, Shield, UserCheck, UserX, Clock, Pencil, Trash2,
  RefreshCw, ChevronRight, BarChart3, FileSpreadsheet,
  UserPlus, AlertCircle, CheckCircle2, XCircle, Calendar,
  Briefcase, TrendingUp, FileText
} from 'lucide-react';
import { cn } from '../lib/utils';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';

const API = process.env.REACT_APP_BACKEND_URL;

const DEPARTMENTS = [
  { value: 'management', label: 'Management' }, { value: 'sales', label: 'Sales' },
  { value: 'marketing', label: 'Marketing' }, { value: 'operations', label: 'Operations' },
  { value: 'support', label: 'Support' }, { value: 'engineering', label: 'Engineering' },
  { value: 'hr', label: 'HR' }, { value: 'finance', label: 'Finance' },
  { value: 'legal', label: 'Legal' }, { value: 'product', label: 'Product' },
  { value: 'design', label: 'Design' }, { value: 'customer_success', label: 'Customer Success' },
  { value: 'logistics', label: 'Logistics' }, { value: 'field_service', label: 'Field Service' },
  { value: 'other', label: 'Other' },
];

const ACCESS_LEVELS = [
  { value: 'staff', label: 'Staff', color: 'bg-blue-100 text-blue-700' },
  { value: 'manager', label: 'Manager', color: 'bg-purple-100 text-purple-700' },
  { value: 'hr_admin', label: 'HR Admin', color: 'bg-amber-100 text-amber-700' },
];

const STATUS_CONFIG = {
  active: { label: 'Active', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
  invited: { label: 'Invited', color: 'bg-blue-100 text-blue-700', icon: Mail },
  on_leave: { label: 'On Leave', color: 'bg-amber-100 text-amber-700', icon: Clock },
  suspended: { label: 'Suspended', color: 'bg-orange-100 text-orange-700', icon: AlertCircle },
  terminated: { label: 'Terminated', color: 'bg-red-100 text-red-700', icon: XCircle },
  pending_approval: { label: 'Pending', color: 'bg-gray-100 text-gray-700', icon: Clock },
};

const EMPLOYMENT_TYPES = [
  { value: 'full_time', label: 'Full-Time' },
  { value: 'part_time', label: 'Part-Time' },
  { value: 'contract', label: 'Contract' },
  { value: 'intern', label: 'Intern' },
  { value: 'freelance', label: 'Freelance' },
];

const COUNTRIES = [
  'Afghanistan','Albania','Algeria','Argentina','Australia','Austria','Bahrain','Bangladesh','Belgium','Brazil',
  'Canada','China','Colombia','Czech Republic','Denmark','Egypt','Ethiopia','Finland','France','Germany',
  'Ghana','Greece','Hong Kong','Hungary','India','Indonesia','Iraq','Ireland','Israel','Italy',
  'Japan','Jordan','Kenya','Kuwait','Lebanon','Libya','Malaysia','Mexico','Morocco','Nepal',
  'Netherlands','New Zealand','Nigeria','Norway','Oman','Pakistan','Peru','Philippines','Poland','Portugal',
  'Qatar','Romania','Russia','Saudi Arabia','Singapore','South Africa','South Korea','Spain','Sri Lanka',
  'Sudan','Sweden','Switzerland','Syria','Taiwan','Thailand','Tunisia','Turkey','UAE','UK',
  'USA','Ukraine','Uruguay','Uzbekistan','Venezuela','Vietnam','Yemen','Zambia','Zimbabwe'
].map(c => ({ value: c, label: c }));

const CURRENCIES = [
  { value: 'USD', label: 'USD - US Dollar' }, { value: 'EUR', label: 'EUR - Euro' }, { value: 'GBP', label: 'GBP - British Pound' },
  { value: 'AED', label: 'AED - UAE Dirham' }, { value: 'SAR', label: 'SAR - Saudi Riyal' }, { value: 'BHD', label: 'BHD - Bahraini Dinar' },
  { value: 'QAR', label: 'QAR - Qatari Riyal' }, { value: 'KWD', label: 'KWD - Kuwaiti Dinar' }, { value: 'OMR', label: 'OMR - Omani Rial' },
  { value: 'EGP', label: 'EGP - Egyptian Pound' }, { value: 'INR', label: 'INR - Indian Rupee' }, { value: 'PHP', label: 'PHP - Philippine Peso' },
  { value: 'SGD', label: 'SGD - Singapore Dollar' }, { value: 'MYR', label: 'MYR - Malaysian Ringgit' }, { value: 'AUD', label: 'AUD - Australian Dollar' },
  { value: 'CAD', label: 'CAD - Canadian Dollar' }, { value: 'JPY', label: 'JPY - Japanese Yen' }, { value: 'CNY', label: 'CNY - Chinese Yuan' },
  { value: 'KES', label: 'KES - Kenyan Shilling' }, { value: 'NGN', label: 'NGN - Nigerian Naira' }, { value: 'ZAR', label: 'ZAR - South African Rand' },
  { value: 'BRL', label: 'BRL - Brazilian Real' }, { value: 'MXN', label: 'MXN - Mexican Peso' }, { value: 'TRY', label: 'TRY - Turkish Lira' },
];

const StatCard = ({ icon: Icon, label, value, color }) => (
  <Card className="bg-white border-gray-border" data-testid={`stat-${typeof label === 'string' ? label.toLowerCase().replace(/\s/g, '-') : 'metric'}`}>
    <CardContent className="p-3 sm:p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs sm:text-sm text-gray-medium">{label}</p>
          <p className="text-xl sm:text-2xl font-bold text-text-primary mt-1">{value}</p>
        </div>
        <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center", color)}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </CardContent>
  </Card>
);

const StaffRow = ({ member, onView, onEdit, onDelete, onResendInvite }) => {
  const statusCfg = STATUS_CONFIG[member.status] || STATUS_CONFIG.active;
  const accessCfg = ACCESS_LEVELS.find(a => a.value === member.access_level) || ACCESS_LEVELS[0];
  const fullName = [member.first_name, member.last_name].filter(Boolean).join(' ');

  return (
    <div
      className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 bg-white border border-gray-border rounded-xl hover:shadow-sm transition-shadow cursor-pointer"
      onClick={() => onView(member)}
      data-testid={`staff-row-${member.id}`}
    >
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div className="w-10 h-10 rounded-full bg-emerald-brand/10 flex items-center justify-center flex-shrink-0">
          <span className="text-sm font-bold text-emerald-brand">
            {(member.first_name?.[0] || '').toUpperCase()}{(member.last_name?.[0] || '').toUpperCase()}
          </span>
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-medium text-text-primary truncate">{fullName || member.email}</p>
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            <span className="text-xs text-gray-medium">{member.job_title || 'No title'}</span>
            {member.department && (
              <>
                <span className="text-xs text-gray-300">|</span>
                <span className="text-xs text-gray-medium capitalize">{member.department}</span>
              </>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-3 flex-wrap sm:flex-nowrap flex-shrink-0">
        {member.email && (
          <div className="flex items-center gap-1 text-xs text-gray-medium">
            <Mail className="h-3 w-3" />
            <span className="truncate max-w-[140px]">{member.email}</span>
          </div>
        )}
        <Badge className={cn("text-[10px]", accessCfg.color)}>{accessCfg.label}</Badge>
        <Badge className={cn("text-[10px]", statusCfg.color)}>{statusCfg.label}</Badge>
        <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
          {member.status === 'invited' && onResendInvite && (
            <Button variant="outline" size="sm" className="h-7 text-xs gap-1 text-blue-600 border-blue-200 hover:bg-blue-50"
              onClick={() => onResendInvite(member)} data-testid={`resend-invite-${member.id}`}>
              <Mail className="h-3 w-3" /> Resend
            </Button>
          )}
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onEdit(member)} data-testid={`edit-staff-${member.id}`}>
            <Pencil className="h-3.5 w-3.5 text-gray-medium" />
          </Button>
          <Button variant="ghost" size="icon" className="h-7 w-7 hover:text-red-500" onClick={() => onDelete(member.id)} data-testid={`delete-staff-${member.id}`}>
            <Trash2 className="h-3.5 w-3.5 text-gray-medium" />
          </Button>
        </div>
      </div>
    </div>
  );
};

const COMMON_POSITIONS = [
  'CEO / Founder', 'COO', 'CFO', 'CTO', 'General Manager', 'Operations Manager',
  'Sales Manager', 'Sales Representative', 'Account Executive', 'Business Development Manager',
  'Marketing Manager', 'Marketing Coordinator', 'Social Media Manager', 'Content Creator',
  'HR Manager', 'HR Coordinator', 'Recruiter', 'Office Manager', 'Office Administrator',
  'Project Manager', 'Product Manager', 'Team Lead', 'Supervisor',
  'Customer Service Manager', 'Customer Service Representative', 'Support Specialist',
  'Accountant', 'Finance Manager', 'Bookkeeper', 'Accounts Receivable',
  'IT Manager', 'Software Developer', 'Systems Administrator', 'Data Analyst',
  'Warehouse Manager', 'Logistics Coordinator', 'Delivery Driver', 'Field Technician',
  'Quality Assurance', 'Compliance Officer', 'Legal Counsel',
  'Designer', 'Creative Director', 'Copywriter', 'Photographer',
  'Consultant', 'Trainer', 'Receptionist', 'Executive Assistant', 'Intern',
];

const InviteDialog = ({ open, onOpenChange, onInvited }) => {
  const [form, setForm] = useState({ email: '', first_name: '', last_name: '', job_title: '', department: 'operations', access_level: 'staff' });
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [showCustomTitle, setShowCustomTitle] = useState(false);

  const handleSubmit = async () => {
    if (!form.email || !form.first_name) { toast.error('Email and first name are required'); return; }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/api/staff/invite`, form);
      setResult(data);
      onInvited();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to invite');
    } finally { setSaving(false); }
  };

  const handleClose = (v) => { if (!v) { setResult(null); setForm({ email: '', first_name: '', last_name: '', job_title: '', department: 'operations', access_level: 'staff' }); } onOpenChange(v); };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-emerald-brand" /> Invite Team Member
          </DialogTitle>
        </DialogHeader>
        {result ? (
          <div className="space-y-4 mt-2">
            <div className={`border rounded-xl p-4 text-center ${result.email_sent ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-300'}`}>
              <CheckCircle2 className={`h-8 w-8 mx-auto mb-2 ${result.email_sent ? 'text-emerald-600' : 'text-amber-600'}`} />
              <p className={`text-sm font-medium ${result.email_sent ? 'text-emerald-800' : 'text-amber-800'}`}>
                {result.email_sent ? 'Invitation email sent!' : 'Invite created — email delivery failed'}
              </p>
              <p className={`text-xs mt-1 ${result.email_sent ? 'text-emerald-600' : 'text-amber-700'}`}>
                {result.email_sent
                  ? `We emailed ${form.first_name} a magic-link. The temp credentials below are a backup in case it lands in spam.`
                  : `Please share the invite link and temporary password below with ${form.first_name} directly.`}
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 space-y-2" data-testid="invite-result-credentials">
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-500">Email</span>
                <span className="text-sm font-mono font-medium">{form.email}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-500">Temporary Password</span>
                <div className="flex items-center gap-1.5">
                  <span className="text-sm font-mono font-bold text-emerald-700" data-testid="invite-temp-password">{result.temp_password || '—'}</span>
                  {result.temp_password && (
                    <button
                      onClick={() => { navigator.clipboard.writeText(result.temp_password); toast.success('Password copied'); }}
                      className="text-emerald-600 hover:text-emerald-800 text-[10px] underline"
                      data-testid="copy-temp-password-btn"
                    >Copy</button>
                  )}
                </div>
              </div>
              {result.invite_url && (
                <div className="pt-2 border-t border-gray-200">
                  <div className="text-xs text-gray-500 mb-1">Invite Link (one-click sign-in)</div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-[11px] font-mono text-emerald-700 truncate flex-1" data-testid="invite-url" title={result.invite_url}>{result.invite_url}</span>
                    <button
                      onClick={() => { navigator.clipboard.writeText(result.invite_url); toast.success('Invite link copied'); }}
                      className="text-emerald-600 hover:text-emerald-800 text-[10px] underline flex-shrink-0"
                      data-testid="copy-invite-url-btn"
                    >Copy</button>
                  </div>
                </div>
              )}
            </div>
            <p className="text-[10px] text-gray-400 text-center">The member will be asked to change their password on first login.</p>
            <DialogFooter>
              <Button className="w-full bg-emerald-brand hover:bg-emerald-hover" onClick={() => handleClose(false)}>Done</Button>
            </DialogFooter>
          </div>
        ) : (
        <div className="space-y-4 mt-2">
          <div>
            <Label>Email Address *</Label>
            <Input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="staff@company.com" className="mt-1" data-testid="invite-email" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>First Name *</Label>
              <Input value={form.first_name} onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))} className="mt-1" data-testid="invite-first-name" />
            </div>
            <div>
              <Label>Last Name</Label>
              <Input value={form.last_name} onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))} className="mt-1" data-testid="invite-last-name" />
            </div>
          </div>
          <div>
            <Label>Job Title</Label>
            {showCustomTitle ? (
              <div className="flex gap-2 mt-1">
                <Input value={form.job_title} onChange={e => setForm(f => ({ ...f, job_title: e.target.value }))} placeholder="Type custom job title..." className="flex-1" autoFocus />
                <Button variant="ghost" size="sm" onClick={() => { setShowCustomTitle(false); setForm(f => ({ ...f, job_title: '' })); }}>Back</Button>
              </div>
            ) : (
              <Select value={form.job_title} onValueChange={v => { if (v === '__other__') { setShowCustomTitle(true); setForm(f => ({ ...f, job_title: '' })); } else { setForm(f => ({ ...f, job_title: v })); } }}>
                <SelectTrigger className="mt-1" data-testid="select-job-title"><SelectValue placeholder="Select a position" /></SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {COMMON_POSITIONS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  <SelectItem value="__other__" className="font-medium text-blue-600">Other (type custom)</SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Department</Label>
              <Select value={form.department} onValueChange={v => setForm(f => ({ ...f, department: v }))}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{DEPARTMENTS.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Access Level</Label>
              <Select value={form.access_level} onValueChange={v => setForm(f => ({ ...f, access_level: v }))}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{ACCESS_LEVELS.map(a => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => handleClose(false)}>Cancel</Button>
            <Button className="bg-emerald-brand hover:bg-emerald-hover" onClick={handleSubmit} disabled={saving} data-testid="submit-invite-btn">
              {saving ? 'Sending...' : 'Send Invitation'}
            </Button>
          </DialogFooter>
        </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const BulkUploadDialog = ({ open, onOpenChange, onUploaded }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const handleDownloadTemplate = async () => {
    try {
      const { data } = await axios.get(`${API}/api/staff/bulk-upload/template`);
      const csv = data.headers.join(',') + '\n' + data.headers.map(h => data.sample_row[h] || '').join(',');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = 'staff_template.csv'; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Failed to download template'); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setResult(null);
    try {
      const formData = new FormData(); formData.append('file', file);
      const { data } = await axios.post(`${API}/api/staff/bulk-upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setResult(data);
      if (data.created > 0) { toast.success(`${data.created} team members added`); onUploaded(); }
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={v => { onOpenChange(v); setResult(null); }}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><FileSpreadsheet className="h-5 w-5 text-emerald-brand" /> Bulk Upload</DialogTitle></DialogHeader>
        <div className="space-y-4 mt-2">
          <p className="text-sm text-gray-medium">Upload a CSV file with employee data.</p>
          <Button variant="outline" className="w-full gap-2" onClick={handleDownloadTemplate} data-testid="download-template-btn"><Download className="h-4 w-4" /> Download CSV Template</Button>
          <div className="border-2 border-dashed border-gray-border rounded-lg p-6 text-center">
            <Upload className="h-8 w-8 mx-auto mb-2 text-gray-300" />
            <p className="text-sm text-gray-medium mb-2">Drop file or click to browse</p>
            <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" onChange={handleUpload} className="hidden" />
            <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-file-btn">
              {uploading ? <><RefreshCw className="h-4 w-4 animate-spin mr-2" /> Processing...</> : 'Choose File'}
            </Button>
          </div>
          {result && (
            <Card className={cn("border", result.created > 0 ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50")}>
              <CardContent className="p-3">
                <p className="text-sm font-medium">{result.message}</p>
                <div className="flex gap-4 mt-2 text-xs">
                  <span className="text-emerald-700">Created: {result.created}</span>
                  <span className="text-amber-700">Skipped: {result.skipped}</span>
                </div>
                {result.errors?.length > 0 && (
                  <div className="mt-2 max-h-24 overflow-y-auto">{result.errors.map((err, i) => <p key={i} className="text-xs text-red-600">{err}</p>)}</div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Hoisted to module scope — defining a component INSIDE another component
// (where `Field` and `SelectField` previously lived) causes React to remount
// the input on every parent render, producing the "letter-by-letter typing"
// bug. Always declare components at module scope and thread state via props.
const StaffField = ({ label, field, type = 'text', disabled = false, editing, form, member, onChange }) => (
  <div>
    <Label className="text-xs text-gray-medium">{label}</Label>
    {editing && !disabled ? (
      <Input type={type} value={form[field] || ''} onChange={e => onChange(field, e.target.value)} className="mt-1 h-8 text-sm" />
    ) : (
      <p className="text-sm text-text-primary mt-1">{member[field] || '—'}</p>
    )}
  </div>
);

const StaffSelectField = ({ label, field, options, disabled = false, editing, form, member, onChange }) => (
  <div>
    <Label className="text-xs text-gray-medium">{label}</Label>
    {editing && !disabled ? (
      <Select value={form[field] || ''} onValueChange={v => onChange(field, v)}>
        <SelectTrigger className="mt-1 h-8 text-sm"><SelectValue /></SelectTrigger>
        <SelectContent>{options.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
      </Select>
    ) : (
      <p className="text-sm text-text-primary mt-1 capitalize">{(options.find(o => o.value === member[field])?.label) || member[field] || '—'}</p>
    )}
  </div>
);

const StaffDetailDialog = ({ open, onOpenChange, member, onSaved, staffList = [] }) => {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('personal');
  const [timeline, setTimeline] = useState([]);
  const [onboarding, setOnboarding] = useState(null);
  const [leaveBalance, setLeaveBalance] = useState(null);
  const [completingStep, setCompletingStep] = useState(null);
  const [permissions, setPermissions] = useState({ allowed: [], denied: [] });
  const [allModules, setAllModules] = useState([]);
  const [savingPerms, setSavingPerms] = useState(false);

  useEffect(() => {
    if (member) {
      setForm({ ...member });
      setEditing(false);
      setActiveSection('personal');
      // Fetch HR data for this member
      if (member.email) {
        axios.get(`${API}/api/hr/employees`, { params: { search: member.email } })
          .then(r => {
            const hrEmp = (r.data || []).find(e => e.email === member.email);
            if (hrEmp) {
              setLeaveBalance({ balance: hrEmp.leave_balance, used: hrEmp.leave_used });
              axios.get(`${API}/api/hr/employees/${hrEmp.id}/timeline`).then(tr => setTimeline(tr.data)).catch(() => setTimeline([]));
              axios.get(`${API}/api/hr/onboarding/${hrEmp.id}`).then(ob => setOnboarding(ob.data)).catch(() => setOnboarding(null));
            } else {
              setLeaveBalance(null); setTimeline([]); setOnboarding(null);
            }
          }).catch(() => { setLeaveBalance(null); setTimeline([]); setOnboarding(null); });
      }
      // Fetch permissions
      if (member.id) {
        axios.get(`${API}/api/staff/members/${member.id}/permissions`).then(r => {
          setPermissions({ allowed: r.data.allowed_modules || [], denied: r.data.denied_modules || [] });
        }).catch(() => setPermissions({ allowed: [], denied: [] }));
      }
      // Fetch all modules
      axios.get(`${API}/api/modules/registry`).then(r => setAllModules(r.data.modules || [])).catch(() => {});
    }
  }, [member]);

  const completeOnboardingStep = async (stepOrder) => {
    if (!onboarding) return;
    setCompletingStep(stepOrder);
    try {
      const res = await axios.put(`${API}/api/hr/onboarding/${onboarding.id}/step/${stepOrder}`);
      toast.success(`Step completed (${res.data.progress}%)`);
      setOnboarding(prev => ({ ...prev, progress: res.data.progress, status: res.data.status, steps: prev.steps.map(s => s.order === stepOrder ? { ...s, completed: true } : s) }));
    } catch { toast.error('Failed'); }
    finally { setCompletingStep(null); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/staff/members/${member.id}`, form);
      toast.success('Profile updated');
      setEditing(false);
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || 'Update failed'); }
    finally { setSaving(false); }
  };

  // Stable callback so hoisted Field/SelectField don't re-mount unnecessarily.
  // MUST be declared before the early return so React's rules-of-hooks are satisfied.
  const handleFieldChange = useCallback((field, value) => {
    setForm(f => ({ ...f, [field]: value }));
  }, []);

  if (!member) return null;
  const lb = leaveBalance?.balance || {};
  const lu = leaveBalance?.used || {};

  // Use the hoisted-stable components — passing `form/member/editing/onChange`
  // through props is what keeps the underlying <Input>/<Select> from being
  // unmounted on every keystroke. (Defining a wrapper component INSIDE this
  // function would resurrect the very bug we are fixing.)
  const fieldProps = { editing, form, member, onChange: handleFieldChange };

  const sections = [
    { id: 'personal', label: 'Personal', icon: Users },
    { id: 'contact', label: 'Contact', icon: Phone },
    { id: 'employment', label: 'Employment', icon: Building2 },
    { id: 'permissions', label: 'Modules', icon: Shield },
    { id: 'leave', label: 'Leave', icon: Calendar },
    { id: 'onboarding', label: 'Onboarding', icon: FileText },
    { id: 'timeline', label: 'Activity', icon: TrendingUp },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="font-heading">{member.first_name} {member.last_name}</DialogTitle>
            <div className="flex items-center gap-2">
              {!editing ? (
                <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid="edit-profile-btn"><Pencil className="h-3.5 w-3.5 mr-1" /> Edit</Button>
              ) : (
                <>
                  <Button size="sm" variant="outline" onClick={() => { setEditing(false); setForm({ ...member }); }}>Cancel</Button>
                  <Button size="sm" className="bg-emerald-brand hover:bg-emerald-hover" onClick={handleSave} disabled={saving} data-testid="save-profile-btn">{saving ? 'Saving...' : 'Save'}</Button>
                </>
              )}
            </div>
          </div>
        </DialogHeader>

        <div className="flex gap-1 overflow-x-auto pb-2 border-b border-gray-border mt-2">
          {sections.map(s => (
            <button key={s.id} onClick={() => setActiveSection(s.id)}
              className={cn("px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap transition-colors flex items-center gap-1.5",
                activeSection === s.id ? "bg-emerald-brand text-white" : "text-gray-medium hover:bg-off-white"
              )}>
              <s.icon className="h-3 w-3" /> {s.label}
            </button>
          ))}
        </div>

        <div className="mt-4 space-y-4">
          {activeSection === 'personal' && (
            <div className="grid grid-cols-2 gap-4">
              <StaffField {...fieldProps} label="First Name" field="first_name" />
              <StaffField {...fieldProps} label="Last Name" field="last_name" />
              <StaffField {...fieldProps} label="Preferred Name" field="preferred_name" />
              <StaffSelectField {...fieldProps} label="Gender" field="gender" options={[
                { value: 'male', label: 'Male' }, { value: 'female', label: 'Female' }, { value: 'other', label: 'Other' }
              ]} />
              <StaffField {...fieldProps} label="Date of Birth" field="date_of_birth" type="date" />
              <StaffSelectField {...fieldProps} label="Nationality" field="nationality" options={COUNTRIES} />
            </div>
          )}

          {activeSection === 'contact' && (
            <div className="grid grid-cols-2 gap-4">
              <StaffField {...fieldProps} label="Work Email" field="email" disabled />
              <StaffField {...fieldProps} label="Personal Email" field="personal_email" />
              <StaffField {...fieldProps} label="Mobile Phone" field="mobile_phone" />
              <StaffField {...fieldProps} label="Work Phone" field="work_phone" />
              <div className="col-span-2"><StaffField {...fieldProps} label="Address" field="address_line1" /></div>
              <StaffField {...fieldProps} label="City" field="city" />
              <StaffSelectField {...fieldProps} label="Country" field="country" options={COUNTRIES} />
              <StaffField {...fieldProps} label="Emergency Contact" field="emergency_contact_name" />
              <StaffField {...fieldProps} label="Emergency Phone" field="emergency_contact_phone" />
            </div>
          )}

          {activeSection === 'employment' && (
            <div className="grid grid-cols-2 gap-4">
              <StaffField {...fieldProps} label="Employee ID" field="employee_id_number" />
              <StaffField {...fieldProps} label="Job Title" field="job_title" />
              <StaffSelectField {...fieldProps} label="Department" field="department" options={DEPARTMENTS} />
              <StaffSelectField {...fieldProps} label="Employment Type" field="employment_type" options={EMPLOYMENT_TYPES} />
              <StaffSelectField {...fieldProps} label="Access Level" field="access_level" options={ACCESS_LEVELS} />
              <StaffField {...fieldProps} label="Hire Date" field="hire_date" type="date" />
              <StaffSelectField {...fieldProps} label="Work Location" field="work_location" options={[
                { value: 'office', label: 'Office' }, { value: 'remote', label: 'Remote' }, { value: 'hybrid', label: 'Hybrid' }, { value: 'field', label: 'Field / Outdoor' }
              ]} />
              <StaffSelectField {...fieldProps} label="Reports To" field="reporting_to" options={[{ value: '', label: 'None' }, ...staffList.map(s => ({ value: `${s.first_name} ${s.last_name}`, label: `${s.first_name} ${s.last_name}` }))]} />
              <StaffField {...fieldProps} label="Salary" field="salary" type="number" />
              <StaffSelectField {...fieldProps} label="Currency" field="salary_currency" options={CURRENCIES} />
              <div className="col-span-2 mt-2 rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="elevate-employee-panel">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <p className="text-xs font-semibold text-amber-900">Elevate to Manager-level access</p>
                    <p className="text-[11px] text-amber-800 mt-0.5 leading-snug">
                      When ON, this employee sees the operational sidebar (Lead Gen, Sales, Customer Service, Operations) —
                      the same view a General Manager gets. Individual page access is still gated by module permissions above.
                    </p>
                  </div>
                  <Switch
                    data-testid="elevate-employee-switch"
                    checked={!!form.is_elevated_employee}
                    disabled={!editing}
                    onCheckedChange={(v) => handleFieldChange('is_elevated_employee', v)}
                  />
                </div>
              </div>
            </div>
          )}

          {activeSection === 'permissions' && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-text-primary">Module Access</p>
                  <p className="text-[10px] text-gray-400">{permissions.denied.length === 0 ? 'Full access (all modules enabled)' : `${permissions.denied.length} module(s) restricted`}</p>
                </div>
                {savingPerms && <RefreshCw className="h-3.5 w-3.5 animate-spin text-emerald-500" />}
              </div>
              {Object.entries(allModules.reduce((acc, m) => { const cat = m.category || 'other'; if (!acc[cat]) acc[cat] = []; acc[cat].push(m); return acc; }, {})).sort(([a],[b]) => a.localeCompare(b)).map(([cat, mods]) => (
                <div key={cat} className="border border-gray-100 rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-3 py-1.5 border-b border-gray-100">
                    <p className="text-[10px] font-semibold uppercase text-gray-500 tracking-wider">{cat.replace(/_/g, ' ')}</p>
                  </div>
                  <div className="divide-y divide-gray-50">
                    {mods.map(m => {
                      const isDenied = permissions.denied.includes(m.module_id);
                      return (
                        <label key={m.module_id} className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={!isDenied}
                            onChange={(e) => {
                              const newDenied = e.target.checked
                                ? permissions.denied.filter(id => id !== m.module_id)
                                : [...permissions.denied, m.module_id];
                              setPermissions(p => ({ ...p, denied: newDenied }));
                              setSavingPerms(true);
                              axios.put(`${API}/api/staff/members/${member.id}/permissions`, { allowed_modules: [], denied_modules: newDenied })
                                .then(() => {}).catch(() => {}).finally(() => setSavingPerms(false));
                            }}
                            className="w-3.5 h-3.5 rounded border-gray-300 text-emerald-brand focus:ring-emerald-brand"
                          />
                          <span className={cn("text-xs", isDenied ? "text-gray-400 line-through" : "text-text-primary")}>{m.name}</span>
                          {m.is_core && <Badge className="bg-blue-100 text-blue-600 text-[8px] h-3.5 px-1">Core</Badge>}
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeSection === 'leave' && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                {['annual', 'sick', 'personal'].map(type => (
                  <div key={type} className="bg-gray-50 rounded-lg p-3 border text-center">
                    <p className="text-xs text-gray-400 capitalize mb-1">{type}</p>
                    <p className="text-lg font-bold">{(lb[type] || 0) - (lu[type] || 0)}</p>
                    <p className="text-[10px] text-gray-400">{lu[type] || 0} used of {lb[type] || 0}</p>
                    <Progress value={lb[type] ? ((lu[type] || 0) / lb[type]) * 100 : 0} className="h-1 mt-1" />
                  </div>
                ))}
              </div>
              {!leaveBalance && (
                <p className="text-xs text-gray-400 text-center py-4">No HR record linked. Leave data unavailable.</p>
              )}
            </div>
          )}

          {activeSection === 'onboarding' && (
            <div className="space-y-3">
              {!onboarding ? (
                <div className="text-center py-6">
                  <FileText className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-400">No onboarding checklist assigned yet.</p>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between">
                    <Badge className={cn("text-xs", onboarding.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700')}>{onboarding.progress}% Complete</Badge>
                    <Progress value={onboarding.progress} className="w-32 h-2" />
                  </div>
                  <div className="space-y-2">
                    {onboarding.steps?.map(step => (
                      <div key={step.order} className={cn("flex items-center gap-3 p-2.5 rounded-lg border transition-all", step.completed ? "bg-emerald-50/50 border-emerald-100" : "bg-white border-gray-100")}>
                        <button onClick={() => !step.completed && completeOnboardingStep(step.order)} disabled={step.completed || completingStep === step.order}
                          className={cn("w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all", step.completed ? "border-emerald-500 bg-emerald-500" : "border-gray-300 hover:border-blue-400")}>
                          {step.completed && <CheckCircle2 className="h-3 w-3 text-white" />}
                        </button>
                        <div className="min-w-0 flex-1">
                          <p className={cn("text-sm font-medium", step.completed && "line-through text-gray-400")}>{step.title}</p>
                          <p className="text-[10px] text-gray-400">{step.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {activeSection === 'timeline' && (
            <div>
              {timeline.length === 0 ? (
                <p className="text-xs text-gray-400 text-center py-6">No activity recorded yet.</p>
              ) : (
                <div className="space-y-0">
                  {timeline.map((a, i) => (
                    <div key={i} className="flex gap-3 py-2.5 border-b border-gray-50 last:border-0">
                      <div className="w-1.5 h-1.5 rounded-full bg-blue-400 mt-1.5 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-text-primary">{a.summary}</p>
                        <p className="text-[10px] text-gray-400 mt-0.5">{a.timestamp ? new Date(a.timestamp).toLocaleString() : ''}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const LeavePanel = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchLeave = useCallback(async () => {
    setLoading(true);
    try { const r = await axios.get(`${API}/api/hr/leave-requests`); setRequests(r.data); }
    catch { /* silent */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchLeave(); }, [fetchLeave]);

  const handleAction = async (reqId, action) => {
    try {
      await axios.put(`${API}/api/hr/leave-requests/${reqId}/${action}`);
      toast.success(`Leave ${action}d`);
      fetchLeave();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const statusColors = { pending: 'bg-amber-100 text-amber-700', approved: 'bg-emerald-100 text-emerald-700', rejected: 'bg-red-100 text-red-700' };

  return (
    <div className="space-y-2" data-testid="leave-panel">
      {requests.length === 0 && !loading && (
        <Card className="bg-white border-gray-border">
          <CardContent className="p-8 text-center">
            <Calendar className="h-10 w-10 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-medium text-sm">No leave requests yet</p>
          </CardContent>
        </Card>
      )}
      {requests.map(r => (
        <div key={r.id} className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-border hover:border-gray-300 transition-all">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-text-primary">{r.employee_name}</p>
            <p className="text-xs text-gray-medium">{r.leave_type} — {r.start_date} to {r.end_date} ({r.days}d)</p>
            {r.reason && <p className="text-[10px] text-gray-400 mt-0.5">{r.reason}</p>}
          </div>
          <Badge className={cn("text-[10px]", statusColors[r.status])}>{r.status}</Badge>
          {r.status === 'pending' && (
            <div className="flex gap-1">
              <Button size="sm" variant="outline" className="h-6 text-[10px] text-emerald-600 border-emerald-200" onClick={() => handleAction(r.id, 'approve')}>Approve</Button>
              <Button size="sm" variant="outline" className="h-6 text-[10px] text-red-600 border-red-200" onClick={() => handleAction(r.id, 'reject')}>Reject</Button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

const DeptChart = ({ data }) => {
  if (!data || data.length === 0) return null;
  const max = Math.max(...data.map(d => d.count));
  const colors = ['#10B981', '#3B82F6', '#8B5CF6', '#F59E0B', '#EF4444', '#06B6D4', '#EC4899', '#84CC16'];
  return (
    <Card className="bg-white border-gray-border" data-testid="dept-chart">
      <CardHeader className="pb-2"><CardTitle className="text-sm font-heading">By Department</CardTitle></CardHeader>
      <CardContent className="space-y-2">
        {data.map((d, i) => (
          <div key={d.department} className="flex items-center gap-3">
            <span className="text-xs text-gray-medium w-24 truncate capitalize">{d.department}</span>
            <div className="flex-1 h-5 bg-gray-100 rounded-full overflow-hidden">
              <div className="h-full rounded-full" style={{ width: `${(d.count / max) * 100}%`, backgroundColor: colors[i % colors.length] }} />
            </div>
            <span className="text-xs font-medium text-text-primary w-6 text-right">{d.count}</span>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

export const StaffManagementPage = () => {
  const [dashboard, setDashboard] = useState(null);
  const [staff, setStaff] = useState([]);
  const [pending, setPending] = useState([]);
  const [hrStats, setHrStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [viewMember, setViewMember] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterDept, setFilterDept] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  const fetchData = useCallback(async () => {
    try {
      const [dashRes, staffRes, pendingRes, hrStatsRes] = await Promise.all([
        axios.get(`${API}/api/staff/dashboard`),
        axios.get(`${API}/api/staff/members`),
        axios.get(`${API}/api/staff/pending`).catch(() => ({ data: { pending: [] } })),
        axios.get(`${API}/api/hr/employees/stats`).catch(() => ({ data: {} })),
      ]);
      setDashboard(dashRes.data);
      setStaff(staffRes.data.staff || []);
      setPending(pendingRes.data.pending || []);
      setHrStats(hrStatsRes.data || {});
    } catch (err) {
      console.error(err);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this team member?')) return;
    try {
      await axios.delete(`${API}/api/staff/members/${id}`);
      toast.success('Team member removed');
      fetchData();
    } catch { toast.error('Failed to remove'); }
  };

  const handleResendInvite = async (member) => {
    try {
      const { data } = await axios.post(`${API}/api/staff/members/${member.id}/resend-invite`);
      if (data.email_sent) {
        toast.success(`Invite resent to ${member.email}`);
      } else {
        toast.info(`Invite resent (email delivery pending). Temp password: ${data.temp_password}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to resend invite');
    }
  };

  const handleApprove = async (id) => {
    try {
      await axios.post(`${API}/api/staff/pending/${id}/approve`, { department: 'other', access_level: 'staff' });
      toast.success('Approved'); fetchData();
    } catch { toast.error('Failed to approve'); }
  };

  const handleReject = async (id) => {
    try {
      await axios.delete(`${API}/api/staff/pending/${id}`);
      toast.success('Rejected'); fetchData();
    } catch { toast.error('Failed'); }
  };

  const filtered = staff.filter(s => {
    if (filterDept !== 'all' && s.department !== filterDept) return false;
    if (filterStatus !== 'all' && s.status !== filterStatus) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const name = `${s.first_name} ${s.last_name}`.toLowerCase();
      return name.includes(q) || (s.email || '').toLowerCase().includes(q) || (s.job_title || '').toLowerCase().includes(q);
    }
    return true;
  });

  if (loading) return (
    <div className="flex items-center justify-center h-64"><RefreshCw className="h-8 w-8 text-emerald-brand animate-spin" /></div>
  );

  const pendingLeave = hrStats.pending_leave_requests || 0;

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="staff-management-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-text-primary">Team & People<PageInfoTooltip pageKey="staff-management" /></h1>
          <p className="text-gray-medium">Manage team members, leave, onboarding, and access levels</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" className="gap-2" onClick={() => setUploadOpen(true)} data-testid="bulk-upload-btn"><Upload className="h-4 w-4" /> Bulk Upload</Button>
          <Button className="bg-emerald-brand hover:bg-emerald-hover gap-2" onClick={() => setInviteOpen(true)} data-testid="invite-staff-btn"><UserPlus className="h-4 w-4" /> Invite Member</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        <StatCard icon={Users} label="Total Team" value={dashboard?.total || 0} color="bg-emerald-100 text-emerald-700" />
        <StatCard icon={UserCheck} label="Active" value={dashboard?.active || 0} color="bg-blue-100 text-blue-700" />
        <StatCard icon={Mail} label="Invited" value={dashboard?.invited || 0} color="bg-purple-100 text-purple-700" />
        <StatCard icon={Clock} label="On Leave" value={dashboard?.on_leave || 0} color="bg-amber-100 text-amber-700" />
        <StatCard icon={Calendar} label="Leave Requests" value={pendingLeave} color="bg-orange-100 text-orange-700" />
      </div>

      {pending.length > 0 && (
        <Card className="bg-amber-50 border-amber-200" data-testid="pending-approvals">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-heading flex items-center gap-2 text-amber-800">
              <AlertCircle className="h-4 w-4" /> Pending Approvals ({pending.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {pending.map(p => (
              <div key={p.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-amber-200">
                <div>
                  <p className="text-sm font-medium text-text-primary">{p.full_name}</p>
                  <p className="text-xs text-gray-medium">{p.email}</p>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" className="bg-emerald-brand hover:bg-emerald-hover h-7 text-xs" onClick={() => handleApprove(p.id)}><CheckCircle2 className="h-3 w-3 mr-1" /> Approve</Button>
                  <Button size="sm" variant="destructive" className="h-7 text-xs" onClick={() => handleReject(p.id)}><XCircle className="h-3 w-3 mr-1" /> Reject</Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="roster" className="space-y-4">
        <TabsList className="bg-white border border-gray-border w-full sm:w-auto overflow-x-auto flex-nowrap justify-start">
          <TabsTrigger value="roster" data-testid="tab-roster" className="text-xs sm:text-sm whitespace-nowrap">Roster ({staff.length})</TabsTrigger>
          <TabsTrigger value="leave" data-testid="tab-leave" className="text-xs sm:text-sm whitespace-nowrap">Leave Requests {pendingLeave > 0 && <Badge className="ml-1.5 bg-amber-100 text-amber-700 text-[10px] h-5 px-1.5">{pendingLeave}</Badge>}</TabsTrigger>
          <TabsTrigger value="overview" data-testid="tab-overview" className="text-xs sm:text-sm whitespace-nowrap">Overview</TabsTrigger>
        </TabsList>

        <TabsContent value="roster" className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-medium" />
              <Input placeholder="Search by name, email, or title..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-9" data-testid="staff-search" />
            </div>
            <Select value={filterDept} onValueChange={setFilterDept}>
              <SelectTrigger className="w-full sm:w-[160px]" data-testid="filter-dept"><SelectValue placeholder="Department" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Departments</SelectItem>
                {DEPARTMENTS.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-full sm:w-[140px]" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="invited">Invited</SelectItem>
                <SelectItem value="on_leave">On Leave</SelectItem>
                <SelectItem value="terminated">Terminated</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {filtered.length > 0 ? (
            <div className="space-y-2">
              {filtered.map(m => (
                <StaffRow key={m.id} member={m}
                  onView={m => { setViewMember(m); setDetailOpen(true); }}
                  onEdit={m => { setViewMember(m); setDetailOpen(true); }}
                  onDelete={handleDelete}
                  onResendInvite={handleResendInvite}
                />
              ))}
            </div>
          ) : (
            <Card className="bg-white border-gray-border">
              <CardContent className="p-8 text-center">
                <Users className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p className="text-gray-medium">{searchQuery || filterDept !== 'all' || filterStatus !== 'all' ? 'No members match your filters' : 'No team members yet'}</p>
                <Button className="bg-emerald-brand hover:bg-emerald-hover mt-4" onClick={() => setInviteOpen(true)}>
                  <UserPlus className="h-4 w-4 mr-2" /> Invite Member
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="leave" className="space-y-4">
          <LeavePanel />
        </TabsContent>

        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <DeptChart data={dashboard?.by_department} />
            <Card className="bg-white border-gray-border" data-testid="access-levels">
              <CardHeader className="pb-2"><CardTitle className="text-sm font-heading">By Access Level</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                {(dashboard?.by_access_level || []).map(a => {
                  const cfg = ACCESS_LEVELS.find(l => l.value === a.level) || ACCESS_LEVELS[0];
                  return (
                    <div key={a.level} className="flex items-center justify-between p-3 bg-off-white rounded-lg">
                      <div className="flex items-center gap-2"><Shield className="h-4 w-4 text-gray-medium" /><span className="text-sm font-medium capitalize">{cfg.label}</span></div>
                      <Badge className={cn("text-xs", cfg.color)}>{a.count}</Badge>
                    </div>
                  );
                })}
                {(!dashboard?.by_access_level || dashboard.by_access_level.length === 0) && <p className="text-sm text-gray-medium text-center py-4">No data yet</p>}
              </CardContent>
            </Card>
            <Card className="bg-white border-gray-border" data-testid="employment-types">
              <CardHeader className="pb-2"><CardTitle className="text-sm font-heading">By Employment Type</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                {(dashboard?.by_employment_type || []).map(t => {
                  const label = EMPLOYMENT_TYPES.find(et => et.value === t.type)?.label || t.type;
                  return (
                    <div key={t.type} className="flex items-center justify-between p-3 bg-off-white rounded-lg">
                      <span className="text-sm font-medium capitalize">{label}</span>
                      <Badge className="bg-blue-100 text-blue-700 text-xs">{t.count}</Badge>
                    </div>
                  );
                })}
                {(!dashboard?.by_employment_type || dashboard.by_employment_type.length === 0) && <p className="text-sm text-gray-medium text-center py-4">No data yet</p>}
              </CardContent>
            </Card>
          </div>
          <Card className="bg-white border-gray-border" data-testid="role-hierarchy">
            <CardHeader className="pb-2"><CardTitle className="text-sm font-heading">Team Role Hierarchy</CardTitle></CardHeader>
            <CardContent>
              <div className="flex flex-wrap items-center gap-2">
                {[
                  { role: 'Subscriber Admin', color: 'bg-blue-100 text-blue-700 border-blue-200' },
                  { role: 'HR Admin', color: 'bg-amber-100 text-amber-700 border-amber-200' },
                  { role: 'Manager', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
                  { role: 'Staff', color: 'bg-gray-100 text-gray-700 border-gray-200' },
                ].map((r, i) => (
                  <div key={r.role} className="flex items-center gap-2">
                    <Badge className={cn("text-xs border", r.color)}>{r.role}</Badge>
                    {i < 3 && <ChevronRight className="h-4 w-4 text-gray-300" />}
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-medium mt-3">Each level inherits permissions from levels below.</p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <InviteDialog open={inviteOpen} onOpenChange={setInviteOpen} onInvited={fetchData} />
      <BulkUploadDialog open={uploadOpen} onOpenChange={setUploadOpen} onUploaded={fetchData} />
      <StaffDetailDialog open={detailOpen} onOpenChange={setDetailOpen} member={viewMember} onSaved={fetchData} staffList={staff} />
    </div>
  );
};

export default StaffManagementPage;
